"use client"

import { useSyncExternalStore } from "react"

/**
 * Global audio settings - persisted volume multipliers for sound effects and
 * music (0..1). Consumed by the SFX layer (use-sound-effects) and both music
 * hooks; edited from the pause menu.
 */

export interface AudioSettings {
  sfx: number
  music: number
}

const STORAGE_KEY = "pelf-audio-settings"
const DEFAULTS: AudioSettings = { sfx: 1, music: 1 }

const clamp01 = (v: number) => Math.max(0, Math.min(1, v))

function load(): AudioSettings {
  if (typeof window === "undefined") return DEFAULTS
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULTS
    const parsed = JSON.parse(raw) as Partial<AudioSettings>
    return {
      sfx: typeof parsed.sfx === "number" ? clamp01(parsed.sfx) : DEFAULTS.sfx,
      music: typeof parsed.music === "number" ? clamp01(parsed.music) : DEFAULTS.music,
    }
  } catch {
    return DEFAULTS
  }
}

let settings: AudioSettings = load()
const listeners = new Set<() => void>()

function persist(): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
  } catch {
    // Private browsing / storage full - settings just won't survive reload
  }
}

export function getAudioSettings(): AudioSettings {
  return settings
}

/** Volume multiplier for sound effects (0..1) */
export function getSfxVolume(): number {
  return settings.sfx
}

/** Volume multiplier for music (0..1) */
export function getMusicVolume(): number {
  return settings.music
}

export function setAudioSettings(update: Partial<AudioSettings>): void {
  settings = {
    sfx: update.sfx !== undefined ? clamp01(update.sfx) : settings.sfx,
    music: update.music !== undefined ? clamp01(update.music) : settings.music,
  }
  persist()
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Reactive accessor for components (pause menu sliders, music hooks) */
export function useAudioSettings(): AudioSettings {
  return useSyncExternalStore(subscribe, getAudioSettings, () => DEFAULTS)
}
